<svelte:options runes={false} />
<script context="module">
  // ---- Symbol-format conversion helpers (U1 / KTD1b) ----
  // The delimiter seam now lives in lib/symbolFormat.js (TASK-20260823: DOM-free lib modules need it
  // under Vitest). Re-exported here so the picker stays the import site every form already uses.
  import { toRequestSymbol, fromRequestSymbol } from '../../lib/symbolFormat.js';
  export { toRequestSymbol, fromRequestSymbol };
</script>

<script>
  import { onDestroy } from 'svelte';
  import { symbols, loading } from '../../lib/store.js';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';
  import { filterSymbolsByQuote } from '../../lib/quoteCurrency.js';

  // ---- Props (U2 / U4 contract) ----
  // `selected` is the single source of truth: an array of slash-form pair strings, two-way bound from the
  // form. In runes={false} mode the child must REASSIGN selected (not mutate in place) for the bind to
  // propagate — every add/remove below builds a fresh array (KTD1 reactivity note).
  export let selected = [];
  export let max = 20;
  export let invalid = false;
  export let disabled = false;
  export let id = 'symbol-multiselect';   // required for the form's <label for> to target the combobox
  export let describedby = undefined;
  // ADR-0024 §2: the selected strategy derives its own universe, so the operator must not pick pairs.
  // Renders disabled AND blank (not merely greyed-over-a-stale-selection) with `lockedNote` explaining
  // what picks the symbols instead. The server enforces this independently — see ADR-0024 §3.
  export let locked = false;
  export let lockedNote = '';
  // ADR-0014: when set (e.g. 'USDC'), only pairs QUOTED in this currency are offered — the strategy's
  // single quote currency constrains its whole tradable universe. Null → unfiltered (legacy callers).
  export let quoteCurrency = null;

  // A locked picker is disabled AND empty: leaving a stale hand-picked list visible (or worse, in the
  // submitted request) is exactly the confusion this removes. Clearing it here also means the form
  // sends no symbols, which is what the self-universe backtest path expects.
  $: if (locked) {
    if (selected.length > 0) selected = [];
    if (open) open = false;
  }
  $: isDisabled = disabled || locked;

  let searchTerm = '';
  let open = false;
  let highlightIndex = -1;
  let rootEl;                 // wrapper, for click-outside hit-testing
  let inputEl;               // the combobox input, for focus return on Escape/Done
  let listEl;                // the listbox, for scroll-into-view

  const listboxId = `${id}-listbox`;

  // ---- Derived filter (mirrors CryptoList.svelte's predicate: symbol / baseAssetName / quoteAssetName),
  //      pre-narrowed to the strategy's quote currency when one is set (ADR-0014). ----
  $: search = searchTerm.trim().toLowerCase();
  $: quoteFiltered = filterSymbolsByQuote($symbols, quoteCurrency);
  $: filtered = quoteFiltered.filter((s) => {
    if (!search) return true;
    return (
      s.symbol?.toLowerCase().includes(search) ||
      s.baseAssetName?.toLowerCase().includes(search) ||
      s.quoteAssetName?.toLowerCase().includes(search)
    );
  });

  $: atMax = selected.length >= max;
  $: selectedSet = new Set(selected);

  // Three distinct store states so loading never renders as "no pairs available" (design review).
  // 'loading'   — store empty AND a fetch is in flight
  // 'empty'     — store genuinely empty (loaded, nothing returned)
  // 'no-match'  — store has pairs but the search matched none
  // 'has-rows'  — normal
  $: storeState =
    $symbols.length === 0
      ? ($loading ? 'loading' : 'empty')
      : filtered.length === 0
        ? 'no-match'
        : 'has-rows';

  // When the filtered set changes (the list reorders or shrinks), drop the highlight rather than clamping
  // it to a boundary — a clamped index would point at an unrelated row, so Enter could toggle the wrong
  // pair and aria-activedescendant could reference a vanished option id. Tracking `filtered` by reference
  // in a reactive resets the highlight whenever the result set is rebuilt.
  $: resetHighlightOn(filtered);
  function resetHighlightOn(_) {
    highlightIndex = -1;
  }

  // A store row's `symbol` is not guaranteed slash-form at runtime — loadSymbols() in App.svelte
  // defensively checks both BTC-USD and BTC/USD. Normalize every store symbol to slash form before it
  // enters `selected`, so the picker's internal representation (and applyParams' fromRequestSymbol output)
  // always agree. Without this, a dash-form store row added as a chip would never match a prefilled
  // slash-form selection — isSelected would fail, duplicates would slip in, and the ✓ would never show.
  function normalize(pair) {
    return fromRequestSymbol(pair);
  }

  function isSelected(pair) {
    return selectedSet.has(normalize(pair));
  }

  function addPair(pair) {
    const norm = normalize(pair);
    if (selectedSet.has(norm)) return;
    if (selected.length >= max) return; // affordance only; form owns the authoritative error
    selected = [...selected, norm];
    // Clear the query so the just-picked term doesn't linger in the box (U1) — the next search starts fresh.
    searchTerm = '';
  }

  function removePair(pair) {
    const norm = normalize(pair);
    selected = selected.filter((p) => p !== norm);
  }

  function togglePair(pair) {
    if (isSelected(pair)) removePair(pair);
    else addPair(pair);
  }

  function removeLast() {
    if (selected.length === 0) return;
    selected = selected.slice(0, -1);
  }

  // ---- Open / close ----
  function openDropdown() {
    if (isDisabled) return;
    open = true;
  }

  function closeDropdown({ refocus = false } = {}) {
    open = false;
    highlightIndex = -1;
    if (refocus && inputEl) inputEl.focus();
  }

  function onInput() {
    openDropdown();
    highlightIndex = -1;
  }

  // Done explicitly dismisses the dropdown. We must NOT refocus the input here: input's on:focus reopens
  // the dropdown, so refocusing would immediately undo the close. Blur the input instead so the field
  // settles closed and a later click/focus is what reopens it.
  function onDone() {
    closeDropdown();
    if (inputEl) inputEl.blur();
  }

  function onFieldClick() {
    if (isDisabled) return;
    openDropdown();
    if (inputEl) inputEl.focus();
  }

  // ---- Icon helpers (mirror CryptoList.getIconInfo / getGradient) ----
  // Normalize to slash form first so the base split works whether the store delivers BTC/USD or BTC-USD.
  function baseOf(crypto) {
    return normalize(crypto.symbol ?? '').split('/')[0];
  }
  function iconInfo(crypto) {
    return getCryptoIconOrLetters(baseOf(crypto), crypto.baseAssetName);
  }
  function gradient(crypto) {
    return getCryptoGradient(baseOf(crypto));
  }

  // ---- Keyboard (U3) ----
  function scrollHighlightIntoView() {
    if (!listEl || highlightIndex < 0) return;
    const row = listEl.querySelector(`[data-index="${highlightIndex}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }

  function onKeydown(event) {
    if (isDisabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) {
          openDropdown();
          highlightIndex = filtered.length > 0 ? 0 : -1;
        } else if (filtered.length > 0) {
          highlightIndex = Math.min(highlightIndex + 1, filtered.length - 1);
        }
        scrollHighlightIntoView();
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (open && filtered.length > 0) {
          // From no-highlight (-1), ArrowUp lands on the last row; otherwise step up and stop at the top.
          highlightIndex = highlightIndex <= 0 ? filtered.length - 1 : highlightIndex - 1;
          scrollHighlightIntoView();
        }
        break;

      case 'Enter':
        // Don't submit the surrounding form when toggling a row.
        if (open && highlightIndex >= 0 && highlightIndex < filtered.length) {
          event.preventDefault();
          togglePair(filtered[highlightIndex].symbol);
        }
        break;

      case 'Escape':
        if (open) {
          event.preventDefault();
          closeDropdown({ refocus: true });
        }
        break;

      case 'Backspace':
        // Only eat a chip when the search box is empty — otherwise this is normal text editing.
        if (searchTerm.length === 0 && selected.length > 0) {
          event.preventDefault();
          removeLast();
        }
        break;
    }
  }

  // ---- Click-outside (U3 / KTD5): inlined scoped listener, attached only while open ----
  function onWindowPointerDown(event) {
    if (rootEl && !rootEl.contains(event.target)) {
      closeDropdown();
    }
  }

  $: if (typeof window !== 'undefined') {
    if (open) window.addEventListener('pointerdown', onWindowPointerDown, true);
    else window.removeEventListener('pointerdown', onWindowPointerDown, true);
  }

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerdown', onWindowPointerDown, true);
    }
  });
</script>

<div class="sms-root" class:disabled={isDisabled} class:locked bind:this={rootEl}>
  {#if locked}
    <!-- Self-universe strategy: an empty, inert field plus the rule that picks symbols instead. Showing
         the rule (rather than just grey) is what stops the operator hunting for the missing control. -->
    <div class="sms-locked" data-testid="symbol-locked">
      <input {id} class="sms-input sms-locked-input" type="text" value="" placeholder="Selected automatically"
        disabled aria-describedby={describedby} readonly />
      {#if lockedNote}<span class="sms-locked-note">{lockedNote}</span>{/if}
    </div>
  {:else}
  <!-- Field: chips + search input. Clicking anywhere in the field opens the dropdown. -->
  <div
    class="sms-field"
    class:invalid
    class:open
    on:click={onFieldClick}
    role="presentation"
  >
    {#each selected as pair (pair)}
      <span class="sms-chip">
        <span class="sms-chip-label">{pair}</span>
        <button
          type="button"
          class="sms-chip-remove"
          aria-label={`Remove ${pair}`}
          disabled={isDisabled}
          on:click|stopPropagation={() => removePair(pair)}
        >×</button>
      </span>
    {/each}

    <input
      bind:this={inputEl}
      bind:value={searchTerm}
      {id}
      class="sms-input"
      type="text"
      role="combobox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-autocomplete="list"
      aria-activedescendant={open && highlightIndex >= 0 ? `${id}-opt-${highlightIndex}` : undefined}
      aria-invalid={invalid}
      aria-describedby={describedby}
      placeholder={selected.length === 0 ? 'Search pairs…' : ''}
      autocomplete="off"
      disabled={isDisabled}
      on:focus={openDropdown}
      on:input={onInput}
      on:keydown={onKeydown}
    />
  </div>

  {#if open}
    <div class="sms-dropdown" role="presentation">
      <div class="sms-dropdown-head">
        {#if atMax}
          <span class="sms-max-notice">Maximum {max} pairs selected — remove one to add more.</span>
        {:else}
          <span class="sms-count">{selected.length}/{max} selected</span>
        {/if}
        <button type="button" class="sms-done" on:click={onDone}>Done</button>
      </div>

      {#if storeState === 'loading'}
        <div class="sms-state">Loading pairs…</div>
      {:else if storeState === 'empty'}
        <div class="sms-state">No pairs available</div>
      {:else if storeState === 'no-match'}
        <div class="sms-state">No pairs found</div>
      {:else}
        <ul class="sms-list" id={listboxId} role="listbox" aria-multiselectable="true" bind:this={listEl}>
          {#each filtered as crypto, i (crypto.symbol)}
            {@const sel = isSelected(crypto.symbol)}
            {@const icon = iconInfo(crypto)}
            {@const grad = gradient(crypto)}
            <!-- Keyboard interaction is intentionally on the combobox input (ArrowUp/Down + Enter via
                 aria-activedescendant, APG combobox-with-listbox pattern); real focus stays on the input,
                 so these option rows are mouse-toggle targets only. -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <li
              id={`${id}-opt-${i}`}
              data-index={i}
              class="sms-option"
              class:highlighted={i === highlightIndex}
              class:selected={sel}
              class:disabled-row={atMax && !sel}
              role="option"
              aria-selected={sel}
              on:click|stopPropagation={() => togglePair(crypto.symbol)}
              on:mousemove={() => (highlightIndex = i)}
            >
              {#if icon.type === 'icon'}
                <span class="sms-icon icon-symbol" style="background: {grad}">{icon.value}</span>
              {:else}
                <span class="sms-icon" style="background: {grad}">{icon.value}</span>
              {/if}
              <span class="sms-option-details">
                <span class="sms-option-symbol">{crypto.symbol}</span>
                <span class="sms-option-name">{crypto.baseAssetName}</span>
              </span>
              <span class="sms-check" aria-hidden="true">{sel ? '✓' : ''}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
  {/if}
</div>

<style>
  @import './SymbolMultiSelect.css';
</style>
